import type { BestTimeEntry } from "./best-time-data";
import type { ConditionSafetyState } from "./safety-notes";
import { isSupProfileKey } from "./sport-profiles";

export type BestTimeSafetyFields = Pick<BestTimeEntry, "safetyState">;

export function bestTimeHistoricalSafetyFields(
  sport: string,
): BestTimeSafetyFields | Record<string, never> {
  return isSupProfileKey(sport) ? { safetyState: "no-data" } : {};
}

export function bestTimeEntrySafetyState(
  entry: Pick<BestTimeEntry, "safetyState">,
  sport: string,
): ConditionSafetyState | null {
  if (!isSupProfileKey(sport)) return null;
  return entry.safetyState ?? "no-data";
}

export function isBestTimePromotionEligible(
  entry: Pick<BestTimeEntry, "safetyState">,
  sport: string,
): boolean {
  return bestTimeEntrySafetyState(entry, sport) !== "flagged-red";
}

export type PresentedBestTimeRanking<T> = T & {
  promotionEligible: boolean;
  promotionRank: number | null;
  safetyState: ConditionSafetyState | null;
};

export function presentBestTimeRankings<
  T extends { entry: Pick<BestTimeEntry, "safetyState"> },
>(
  rows: readonly T[],
  sport: string,
): PresentedBestTimeRanking<T>[] {
  const decorated = rows.map((row) => {
    const safetyState = bestTimeEntrySafetyState(row.entry, sport);
    return {
      ...row,
      promotionEligible: safetyState !== "flagged-red",
      safetyState,
    };
  });
  const ordered = isSupProfileKey(sport)
    ? [
        ...decorated.filter((row) => row.promotionEligible),
        ...decorated.filter((row) => !row.promotionEligible),
      ]
    : decorated;
  let promotionRank = 0;
  return ordered.map((row) => ({
    ...row,
    promotionRank: row.promotionEligible ? ++promotionRank : null,
  }));
}

export function bestTimeSafetyQualification(
  state: ConditionSafetyState | null,
): string | null {
  if (state === "flagged-red") return "Safety flag";
  if (state === "flagged-amber") return "Caution";
  if (state === "no-data") return "Safety evidence unavailable";
  return null;
}
